import { createApi } from "@reduxjs/toolkit/query/react";
import { createCryptoBaseQuery } from "./cryptoBaseQuery";

const getCurrentAccessToken = () => {
  return localStorage.getItem("accessToken");
};

export const setCurrentAccessToken = (token) => {
  localStorage.setItem("accessToken", token);
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: createCryptoBaseQuery({
    getCurrentAccessToken,
  }),
  tagTypes: ["Project", "Task", "Sprint"],
  endpoints: (builder) => ({
    getUserProfile: builder.query({
      query: (id) => ({
        url: "/auth/getProfile/" + id,
        method: "GET",
      }),
    }),

    loginUser: builder.mutation({
      query: (body) => ({
        url: "/auth/login",
        method: "POST",
        body,
      }),
    }),

    registerUser: builder.mutation({
      query: (body) => ({
        url: "/auth/register",
        method: "POST",
        body,
      }),
    }),
    createTask: builder.mutation({
      query: (body) => ({
        url: "/issue/insert",
        method: "POST",
        body: body,
      }),
      invalidatesTags: ["Task"],
    }),
    updateTask: builder.mutation({
      query: (payload) => ({
        url: "/issue/updateRecord",
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Task"],
    }),
    deleteTask: builder.mutation({
      query: (id) => ({
        url: "/issue/deleteRecord",
        method: "POST",
        body: { issueId: id },
      }),
      invalidatesTags: ["Task"],
    }),
    projectMember: builder.mutation({
      query: (body) => ({
        url: "/projectMember/insertMany",
        method: "POST",
        body,
      }),
    }),
    getOrganizationMembers: builder.query({
      query: (organizationId) => ({
        url: `/organizationMember/getAllRecord?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    getProjects: builder.query({
      query: () => ({
        url: "/project/getAllRecord",
        method: "GET",
      }),
      transformResponse: (response) => {
        console.log("DEBUG: Raw response from /project/getAllRecord:", response);

        const findFirstArray = (obj) => {
          if (Array.isArray(obj)) return obj;
          if (obj && typeof obj === 'object') {
            const nested = obj.data || obj.schema || obj.list || obj.records;
            if (Array.isArray(nested)) return nested;
            if (nested && typeof nested === 'object') return findFirstArray(nested);

            for (const key in obj) {
              if (Array.isArray(obj[key])) return obj[key];
              if (obj[key] && typeof obj[key] === 'object') {
                const found = findFirstArray(obj[key]);
                if (found) return found;
              }
            }
          }
          return null;
        };

        const rawArray = findFirstArray(response) || [];
        console.log("DEBUG: Extracted projects array:", rawArray);

        let mapped = rawArray.map(item => item.schema || item);
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const userObj = JSON.parse(userStr);
            const currentUserId = userObj.id || userObj._id || userObj.userId;
            if (currentUserId) {
              mapped = mapped.filter(proj => proj.userId === currentUserId || proj.ownerId === currentUserId);
            }
          }
        } catch (e) {
          console.error("Filter error:", e);
        }
        return mapped;
      },
      providesTags: ["Project"],
    }),
    getTasks: builder.query({
      query: (projectId) => ({
        url: projectId ? `/issue/getAllRecord?projectId=${projectId}` : "/issue/getAllRecord",
        method: "GET",
      }),
      transformResponse: (response) => {
        console.log("DEBUG: Raw response from /issue/getAllRecord:", response);

        const findFirstArray = (obj) => {
          if (Array.isArray(obj)) return obj;
          if (obj && typeof obj === 'object') {
            // Check common wrappers first
            const nested = obj.data || obj.schema || obj.list || obj.records;
            if (Array.isArray(nested)) return nested;
            if (nested && typeof nested === 'object') return findFirstArray(nested);

            // Search all keys if not found in common wrappers
            for (const key in obj) {
              if (Array.isArray(obj[key])) return obj[key];
              if (obj[key] && typeof obj[key] === 'object') {
                const found = findFirstArray(obj[key]);
                if (found) return found;
              }
            }
          }
          return null;
        };

        const rawArray = findFirstArray(response) || [];
        console.log("DEBUG: Extracted raw array:", rawArray);

        let mapped = rawArray.map(item => item.schema || item);
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const userObj = JSON.parse(userStr);
            const currentUserId = userObj.id || userObj._id || userObj.userId;
            if (currentUserId) {
              mapped = mapped.filter(task => task.userId === currentUserId || task.assigneeId === currentUserId || task.reporterId === currentUserId);
            }
          }
        } catch (e) {
          console.error("Filter error:", e);
        }
        return mapped;
      },
      providesTags: ["Task"],
    }),
    createProject: builder.mutation({
      query: (body) => ({
        url: "/project/insert",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Project"],
    }),
    searchUser: builder.query({
      query: (email) => ({
        url: `/user/searchRecord?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    searchOneRecord: builder.query({
      query: (email) => ({
        url: `/user/searchOneRecord?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    searchOneOrganizationMember: builder.query({
      query: (userId) => ({
        url: `/organizationMember/searchOneRecord?userId=${userId}`,
        method: "GET",
      }),
    }),
    getAllSprints: builder.query({
      query: () => ({
        url: "/sprint/getAllRecord",
        method: "GET",
      }),
      providesTags: ["Sprint"],
    }),
    createSprint: builder.mutation({
      query: (body) => ({
        url: "/sprint/insert",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Sprint"],
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useGetUserProfileQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useProjectMemberMutation,
  useLazySearchUserQuery,
  useLazySearchOneRecordQuery,
  useLazySearchOneOrganizationMemberQuery,
  useGetAllSprintsQuery,
  useCreateSprintMutation,
  useGetProjectsQuery,
  useGetTasksQuery,
  useCreateProjectMutation,
  useGetOrganizationMembersQuery,
} = api;
